import { cloneDeep, flatten, orderBy } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import type { IAddCarePathSettings, IServiceGroupWithProfileSettings } from '@common/interfaces/';
import type { ValidationErrors } from '@frontend/utils/validation';
import { addCarePathProfileSettingsSchema } from '@common/validation/addCarePath';
import { translateKey } from '@frontend/translate';
import { SortDirection } from '@frontend/utils/sortDirection';
import { emptyValidationErrors, validate, validateOne } from '@frontend/utils/validation';
import type { IEmployment } from '../../../../interfaces/employment';
import type { IServiceGroupWithProfiles } from '../../../../interfaces/organizationAddCarePathState';
import { CarePathSettingsModal } from '../../../carePath/modals/CarePathSettingsModal';
import { CollapsibleRolesCard } from '../components/CollapsibleRolesCard';
import { AddEmploymentsToRoleModal } from '../modals/AddEmploymentsToRoleModal';
import { OrganizationAddCarePathContext } from '../organizationAddCarePathContext';

interface IAssignRolesProps {
    employments: IEmployment[];
}

export interface IProfile {
    id: string;
    name: string;
    serviceGroupId: string;
    settings?: IAddCarePathSettings;
}

export interface IAddCarePathDefaultSettingsWithProfileId extends IAddCarePathSettings {
    profileId?: string;
    settingsUpdated?: boolean;
}
export interface IProfileGroupedByServiceGroup {
    profiles: IProfile[];
    serviceGroup: IServiceGroupWithProfileSettings;
}

export function AssignRoles(props: IAssignRolesProps) {
    const [profilesGroupedByServiceGroup, setProfilesGroupedByServiceGroup] = useState<IProfileGroupedByServiceGroup[]>(
        []
    );
    const [selectedProfile, setSelectedProfile] = useState<IProfile>();
    const [sortDirection, setSortDirection] = useState(SortDirection.asc);
    const [showModal, setShowModal] = useState(false);
    const [currentlyEditingProfileId, setCurrentlyEditingProfileId] = useState<string | null>(null);
    const [settings, setSettings] = useState<IAddCarePathDefaultSettingsWithProfileId | null>(null);
    const [settingErrors, setSettingErrors] = useState<ValidationErrors>(emptyValidationErrors);

    const { addCarePathState, dispatchAddCarePathAction } = useContext(OrganizationAddCarePathContext);

    useEffect(() => {
        loadProfiles();
    }, [addCarePathState.serviceGroups]);

    if (!addCarePathState.episodeCategory || !addCarePathState.serviceGroups) {
        return null;
    }

    return (
        <>
            {profilesGroupedByServiceGroup.map(renderServiceGroup)}
            {selectedProfile && (
                <AddEmploymentsToRoleModal
                    employments={getSelectableEmployments()}
                    onClose={closeModal}
                    onSortEmployments={sortEmployments}
                    saveSelectedEmployments={(employments) => saveSelectedEmployments(employments)}
                    selectedEmploymentIds={getSelectedEmploymentIdsInCurrentProfile()}
                    selectedProfile={selectedProfile}
                    sortDirection={sortDirection}
                />
            )}
            {showModal && settings && (
                <CarePathSettingsModal
                    episodeCategory={addCarePathState.episodeCategory}
                    errors={settingErrors}
                    onCancel={onClickCloseSettingsModal}
                    onChange={handleChangeSettings}
                    onSave={saveSettings}
                    settings={settings}
                />
            )}
        </>
    );

    function renderServiceGroup(serviceGroupWithProfiles: IProfileGroupedByServiceGroup) {
        return (
            <CollapsibleRolesCard
                addCarePathState={addCarePathState}
                defaultProfileSettings={addCarePathState.defaultProfileSettings}
                key={serviceGroupWithProfiles.serviceGroup.id}
                onChangePractitioner={onChangePractitioner}
                onClickSettings={(profileId) => onClickSettings(profileId, serviceGroupWithProfiles)}
                serviceGroupWithProfiles={serviceGroupWithProfiles}
            />
        );
    }

    function onClickCloseSettingsModal() {
        setSettingErrors(emptyValidationErrors);
        setCurrentlyEditingProfileId(null);
        setSettings(null);
        setShowModal(!showModal);
    }

    function handleChangeSettings(updatedSettings: IAddCarePathSettings, errorPath: string) {
        setSettings({ ...updatedSettings });
        validateOne(addCarePathProfileSettingsSchema, settings, errorPath, settingErrors, setSettingErrors);
    }

    async function onClickSettings(profileId: string, serviceGroupWithProfiles: IProfileGroupedByServiceGroup) {
        let settingsToEdit: IAddCarePathSettings;

        const serviceGroupInState =
            addCarePathState.serviceGroups &&
            addCarePathState.serviceGroups.find(
                (serviceGroup) => serviceGroup.id === serviceGroupWithProfiles.serviceGroup.id
            );
        const profileToEditInStateProfiles =
            serviceGroupInState?.profiles && serviceGroupInState?.profiles.find((profile) => profile.id === profileId);

        if (profileToEditInStateProfiles && profileToEditInStateProfiles.settings) {
            settingsToEdit = cloneDeep(profileToEditInStateProfiles.settings);

            setSettings(settingsToEdit);
            setCurrentlyEditingProfileId(profileId);
            setShowModal(!showModal);
        } else {
            console.error('no profileToEditInStateProfiles', profileId, serviceGroupWithProfiles);
        }
    }

    function saveSettings() {
        const { errors } = validate(addCarePathProfileSettingsSchema, settings);

        if (errors.size > 0) {
            console.log(`Save advanced settings fails for profile '${currentlyEditingProfileId}'`, errors); // TODO: Remove after errors cannot be hidden: https://ksyos-it.atlassian.net/browse/CS-12203
            setSettingErrors(errors);
        } else {
            setSettingErrors(emptyValidationErrors);
            const updatedServiceGroups: IServiceGroupWithProfiles[] = [];
            const updatedProfilesGroupedByServiceGroup = profilesGroupedByServiceGroup.map(
                (profileGroupedByServiceGroup) => {
                    const profileToUpdateIndex = profileGroupedByServiceGroup.profiles.findIndex(
                        (profile) => profile.id === currentlyEditingProfileId
                    );
                    if (profileToUpdateIndex > -1 && settings) {
                        profileGroupedByServiceGroup.profiles[profileToUpdateIndex] = {
                            ...profileGroupedByServiceGroup.profiles[profileToUpdateIndex],
                            settings: settings as IAddCarePathSettings,
                        };
                    }
                    updatedServiceGroups.push({
                        ...profileGroupedByServiceGroup.serviceGroup,
                        profiles: profileGroupedByServiceGroup.profiles,
                    });

                    return profileGroupedByServiceGroup;
                }
            );

            setProfilesGroupedByServiceGroup([...updatedProfilesGroupedByServiceGroup]);

            dispatchAddCarePathAction({ type: 'setSettings', serviceGroups: updatedServiceGroups });

            onClickCloseSettingsModal();
        }
    }

    async function sortEmployments(newSortKey: string, newSortDirection: SortDirection) {
        setSortDirection(newSortDirection);
    }

    async function saveSelectedEmployments(newSelectedEmployments: string[]) {
        dispatchAddCarePathAction({ type: 'setRoles', roles: getUpdatedRoles(newSelectedEmployments) });
        closeModal();
    }

    function onChangePractitioner(profileId: string) {
        const profile = flatten(profilesGroupedByServiceGroup.map((serviceGroup) => serviceGroup.profiles)).find(
            (profilePractitionerId) => profilePractitionerId.id === profileId
        );

        if (profile) {
            setSelectedProfile(profile);
        }
    }

    function closeModal() {
        setSelectedProfile(undefined);
        setSortDirection(SortDirection.asc);
    }

    function getUpdatedRoles(newSelectedEmployments: string[]) {
        if (!selectedProfile) {
            return [];
        }
        const updatedRoles = [...(addCarePathState.roles ?? [])].filter(
            (role) => role.profileId !== selectedProfile?.id
        );
        if (newSelectedEmployments.length) {
            const employmentsToStore = props.employments.filter((employment) =>
                newSelectedEmployments.includes(employment.id)
            );

            const currentRoleWithSelectedEmployments = {
                serviceGroupId: selectedProfile.serviceGroupId,
                profileId: selectedProfile.id,
                employments: employmentsToStore,
            };

            updatedRoles.push(currentRoleWithSelectedEmployments);
        }

        return updatedRoles;
    }

    function getSelectableEmployments() {
        const selectableEmployments = [
            ...getSelectedEmploymentsInCurrentProfile(),
            ...getNonSelectedEmploymentsInCurrentServiceGroup(),
        ];
        const orderedSelectableEmployments = orderBy(
            selectableEmployments,
            [isSelectedEmployment, getPractitionerProfession, getPractitionerFamilyName],
            [SortDirection.desc, sortDirection, sortDirection]
        );

        return orderedSelectableEmployments;
    }

    function getSelectedEmploymentsInCurrentProfile() {
        const selectedEmploymentsInCurrentProfile = flatten(
            (addCarePathState.roles ?? [])
                .filter((role) => role.profileId === selectedProfile!.id)
                .map((role) => role.employments)
        );

        return selectedEmploymentsInCurrentProfile;
    }

    function getNonSelectedEmploymentsInCurrentServiceGroup() {
        const selectedEmploymentsInCurrentServiceGroup = flatten(
            (addCarePathState.roles ?? [])
                .filter((role) => role.serviceGroupId === selectedProfile?.serviceGroupId)
                .map((role) => role.employments)
        );

        return props.employments.filter((employment) => !selectedEmploymentsInCurrentServiceGroup.includes(employment));
    }

    function isSelectedEmployment(employment: IEmployment) {
        return [...getSelectedEmploymentIdsInCurrentProfile()].includes(employment.id);
    }

    function getPractitionerProfession(employment: IEmployment) {
        return translateKey(`professionEnum.${employment.profession}`);
    }

    function getPractitionerFamilyName(employment: IEmployment) {
        return employment.practitioner?.nameFamily;
    }

    function getSelectedEmploymentIdsInCurrentProfile() {
        return getSelectedEmploymentsInCurrentProfile().map((employment) => employment.id);
    }

    async function loadProfiles() {
        const groupedServiceGroups: IProfileGroupedByServiceGroup[] = (addCarePathState.serviceGroups ?? []).map(
            (serviceGroup) => ({
                serviceGroup: cloneDeep(serviceGroup),
                profiles: cloneDeep(serviceGroup.profiles ?? []),
            })
        );

        setProfilesGroupedByServiceGroup(groupedServiceGroups);
    }
}
